import {Component, OnInit, ViewChild} from '@angular/core';
import {finalize, forkJoin, map, merge, Observable, switchMap, tap} from 'rxjs';
import {AuthorizationServiceProvider} from 'src/app/auth_profile/services/authorization-service.provider';
import {CalendarEntry, ClickEvent} from 'src/app/col/model/calendar';
import {RecurringType, RecurringTypeUtils} from 'src/app/col/model/v3/booking';
import { LangProductMapper } from 'src/app/col/utils/lang-mappers';
import { Dates } from 'src/app/utils/calendar-utils';
import {Utils} from 'src/app/col/utils/lesson-utils';
import { ModalComponent } from 'src/app/utils/modal/modal.component';
import { SpinnerService } from 'src/app/utils/services/spinner.service';
import {ProductAvailabilityRequest, SimpleProductAvailability, TeacherColLessonScheduleEvents} from "../../../model/v3/booking";
import { Page, Pageable } from 'src/app/utils/pageable';
import { Router } from '@angular/router';
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {ColNativeV3Api} from "../../../services/api/v3/col-native-v3-api";
import * as LessonsV3 from '../../../model/v3/lessons';
import {CourseProductMapper} from '../../../utils/course-product-mapper';

@Component({
    selector: 'app-teacher-main-calendar',
    templateUrl: './teacher-main-calendar.component.html',
    styleUrls: ['./teacher-main-calendar.component.scss'],
    standalone: false
})
export class TeacherMainCalendarComponent implements OnInit {
  private static readonly HOUR_MS = 60 * 60 * 1000;
  private static readonly DAY_MS = 24 * TeacherMainCalendarComponent.HOUR_MS;
  // Both lists back the student lookup behind a calendar entry click, split by planned start
  // date, so InProgress belongs to both: whichever side of now the lesson sits on, the click
  // has to resolve its student.
  private readonly upcomingLessonStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Initializing,
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
    LessonsV3.LessonStatus.Cancel,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];
  private readonly pastLessonStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Initializing,
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
    LessonsV3.LessonStatus.Cancel,
    LessonsV3.LessonStatus.Complete,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];

  weekDays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  @ViewChild('createWorktimeModal', { static: true })
  createWorktimeModal: ModalComponent;
  @ViewChild('availableWorktimeModal', { static: true })
  availableWorktimeModal: ModalComponent;

  private _teacherId: number;
  _focusDate: Date;
  loading = true;
  calendarEntries: CalendarEntry[] = [];
  teacherCourseProfiles: LessonsV3.V3CourseTeacherProfile[] = [];
  availableProducts: string[];

  availabilityToDelete: SimpleProductAvailability;
  deleteRecurring = false;
  allProductsCode = '__all__';
  private eventIdToDelete: number;
  eventChosen: CalendarEntry;

  newWorktimeDate: Date;
  newWorktimeFinishTime: string;
  newWorktimeProduct: string = this.allProductsCode;
  newWorktimeStartTime: string;
  newWorktimeDays: { [dayName: string]: boolean };
  newWorktimeOvertake = 0;

  newWorktimeRecurring = RecurringType.Single;
  newRecurringFinishDate: Date;
  recurringTypes = RecurringType;
  recurringUtils = RecurringTypeUtils;
  minRecurring = new Date(new Date().getTime() + TeacherMainCalendarComponent.DAY_MS);

  pageable: Pageable;
  pageablePast: Pageable;
  langCode: string
  pastLessons: Page<LessonsV3.LFViewBase>;
  bookedLessons: Page<LessonsV3.LFViewBase>;
  isLoading: boolean
  dateNow: Date
  schoolId: number

  set focusDate(date: Date) {
    this._focusDate = date;
    this.loadWeekEvents();
  }

  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
    this.loadWeekEvents();
    this.loadTeacherCompetences();
    this.loadLessons()
  }

  constructor(
    private auth: AuthorizationServiceProvider,
    private spinner: SpinnerService,
    private router: Router,
    private provider: NativeServiceApiProvider
  ) {}

  ngOnInit(): void {
    this.auth
      .getAuthDetailsService()
      .pipe(
        switchMap((authDetailsApi) =>
          forkJoin({
            schoolId: authDetailsApi.getSelfSchoolId(),
            teacherId: authDetailsApi.getSelfTeacherId(),
          })
        )
      )
      .subscribe(({schoolId, teacherId}) => {
        this.schoolId = schoolId;
        this.teacherId = teacherId;
      });
  }

  loadTeacherCompetences() {
    if (!this._teacherId) {
      return;
    }

    this.spinner
      .trace<{
        courseProfiles: LessonsV3.V3CourseTeacherProfile[];
        products: LessonsV3.V3Product[];
      }>(
        this.provider.colV3().pipe(
          switchMap((api) =>
            forkJoin({
              courseProfiles: api.getTeacherCourseProfiles(this.schoolId, this._teacherId),
              products: api.getSchoolProducts(this.schoolId),
            })
          )
        )
      )
      .subscribe(({courseProfiles, products}) => {
        this.teacherCourseProfiles = courseProfiles;
        this.availableProducts = this.mapAvailableProducts(
          courseProfiles,
          products
        );
      });
  }

  private mapAvailableProducts(
    courseProfiles: LessonsV3.V3CourseTeacherProfile[],
    products: LessonsV3.V3Product[]
  ): string[] {
    const availableProductCodes = new Set(
      products
        .map((product) => product?.code)
        .filter((productCode): productCode is string => !!productCode)
    );

    return Array.from(
      new Set(
        courseProfiles
          .map((profile) => profile.course?.code)
          .map((courseCode) => CourseProductMapper.toProductCode(courseCode))
          .filter((productCode): productCode is string =>
            !!productCode && availableProductCodes.has(productCode)
          )
      )
    );
  }

  onCalendarClick(event: ClickEvent) {
    if (!event.entry) {
      this.openWorktimeDialog(event.clickDate);
      return;
    }

    if (this.isSimpleProductAvailability(event.entry.relatedObject)) {
      this.handleAvailabilityEntryClick(event.entry);
      return;
    }

    if (event.entry.classes[0].includes('schedule')) {
      this.handleScheduleEntryClick(event.entry);
    }
  }

  private handleAvailabilityEntryClick(entry: CalendarEntry) {
    this.availabilityToDelete = entry.relatedObject;
    this.eventChosen = entry;
    this.eventIdToDelete = entry.eventId;
    this.deleteRecurring = false;
    this.availableWorktimeModal.show();
  }

  private handleScheduleEntryClick(entry: CalendarEntry) {
    this.dateNow = new Date();
    const pastOrBooked = this.dateNow > entry.dateFrom ? 'past' : 'booked';
    const lessonId = entry.relatedObject.events[0].eventData.lesson.lessonId;
    this.provider
      .colV3()
      .pipe(
        switchMap((api) => api.getLessonStudents(lessonId))
      )
      .subscribe((students) => {
        const studentId =
          students?.[0]?.reference?.id ?? this.getStudentId(lessonId, pastOrBooked);
        if (studentId == null) {
          return;
        }
        this.router.navigateByUrl(
          this.getLessonLink(lessonId, studentId)
        ).then();
      });
  }

  openWorktimeDialog(clickDate: Date): any {
    if (clickDate.getTime() < new Date().getTime()) {
      return;
    }
    this.newWorktimeDate = Dates.roundDateIntoMiddleOfClick(clickDate);
    this.newWorktimeStartTime = this.getTimeStr(this.newWorktimeDate);

    const finishingDate = new Date(
      this.newWorktimeDate.getTime() + 8 * TeacherMainCalendarComponent.HOUR_MS
    );
    this.newWorktimeFinishTime = this.getTimeStr(finishingDate);
    this.newWorktimeDays = {};
    RecurringTypeUtils.days.forEach( day => this.newWorktimeDays[day] = true );
    this.createWorktimeModal.show();
  }

  getTimeStr(date: Date): string {
    return (
      date.getHours() + ':' + date.getMinutes().toString().padStart(2, '0')
    );
  }

  loadWeekEvents() {
    if (!this._teacherId || !this._focusDate) {
      return;
    }
    const {weekStart, weekEnd} = this.getWeekRange(this._focusDate);

    this.loading = true;
    this.spinner
      .trace<CalendarEntry[]>(
        this.provider.colV3().pipe(
          switchMap((api) =>
            forkJoin({
              worktimeEntries: this.loadAvailabilityEntries(api, weekStart, weekEnd),
              scheduleEntries: this.loadLessonEntries(api, weekStart, weekEnd),
            })
          ),
          map(({worktimeEntries, scheduleEntries}) =>
            worktimeEntries.concat(scheduleEntries)
          ),
          tap((calendarEntries) => (this.calendarEntries = calendarEntries)),
          finalize(() => (this.loading = false))
        )
      )
      .subscribe();
  }

  private getWeekRange(focusDate: Date): {weekStart: Date; weekEnd: Date} {
    const weekStart = Utils.getWeekStartDate(focusDate);
    const weekEnd = new Date(weekStart.getTime() + 7 * TeacherMainCalendarComponent.DAY_MS);
    return {weekStart, weekEnd};
  }

  private loadAvailabilityEntries(api: ColNativeV3Api, weekStart: Date, weekEnd: Date): Observable<CalendarEntry[]> {
    return api
      .getAvailabilityEvents(
        this.schoolId,
        this._teacherId,
        weekStart,
        weekEnd,
        null
      )
      .pipe(
        map((worktimesArray: SimpleProductAvailability[]) =>
          this.mapSimpleProductAvailabilityToCalendarEntry(worktimesArray)
        )
      );
  }

  private loadLessonEntries(api: ColNativeV3Api, weekStart: Date, weekEnd: Date): Observable<CalendarEntry[]> {
    return api
      .getTeacherColLessonEvents(
        this.schoolId,
        this._teacherId,
        weekStart,
        weekEnd
      )
      .pipe(
        map((schedulesArray: TeacherColLessonScheduleEvents[]) =>
          this.mapSimpleScheduleEventsToCalendarEntry(schedulesArray)
        )
      );
  }

  loadLessons() {
    this.pageable = this.createLessonsPageable('ASC');
    this.pageablePast = this.createLessonsPageable('DESC');
    const now = new Date();

    this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          forkJoin({
            bookedLessons: this.findTeacherLessons(
              api,
              this.upcomingLessonStatuses,
              now,
              null,
              this.pageable
            ),
            pastLessons: this.findTeacherLessons(
              api,
              this.pastLessonStatuses,
              null,
              now,
              this.pageablePast
            ),
          })
        )
      )
      .subscribe(({bookedLessons, pastLessons}) => {
        this.bookedLessons = bookedLessons;
        this.pastLessons = pastLessons;
      });
  }

  private createLessonsPageable(sortDirection: 'ASC' | 'DESC'): Pageable {
    return new Pageable(0, 100, [`metric.details.plannedStartDate,${sortDirection}`]);
  }

  private findTeacherLessons(
    api: ColNativeV3Api,
    statuses: LessonsV3.LessonStatus[],
    dateFrom: Date | null,
    dateTo: Date | null,
    pageable: Pageable
  ): Observable<Page<LessonsV3.LFViewBase>> {
    return api.findLessons(
      LessonsV3.LessonCategory.Col,
      null,
      null,
      null,
      null,
      this._teacherId,
      null,
      null,
      null,
      statuses,
      dateFrom,
      dateTo,
      pageable
    );
  }

  getStudentId(lessonId: number, pastOrBooked: 'past' | 'booked'): number | undefined {
    const lessons = pastOrBooked === 'past' ? this.pastLessons : this.bookedLessons;
    const lesson = lessons?.content?.find((entry: any) => entry?.id === lessonId) as any;
    return lesson?.students?.[0]?.id ?? lesson?.students?.[0]?.reference?.id;
  }

  getLessonLink(lessonId: number, studentId: number) {
    return this.schoolId === 1
      ? `teacher/col-lessons/school/${this.schoolId}/teacher/${this._teacherId}/lesson/${lessonId}/student/${studentId}`
      : `teacher/panel/lessons/lesson/${lessonId}/student/${studentId}`;
  }

  getAvailabilityName(av: SimpleProductAvailability) {
    return `${av.details.product.code}[${av.details.competences.map(c => c.code).join(", ")}]`
  }

  mapSimpleProductAvailabilityToCalendarEntry(schedules: SimpleProductAvailability[]): CalendarEntry[] {
    const narrowIntersected = schedules.length > 1;
    return schedules.reduce((entries, schedule) => {
      schedule.events.forEach((ev) => {
        let entry = new CalendarEntry(
          schedule.schedule.id,
          ev.eventId,
          ev.eventDate,
          new Date(ev.eventDate.getTime() + ev.duration),
          () => this.getAvailabilityName(schedule),
          null,
          schedule,
          'worktime'
        );
        if (narrowIntersected) {
          entry = entry.narrowIntersected();
        }
        entries.push(entry);
      });
      return entries;
    }, [] as CalendarEntry[]);
  }

  private isSimpleProductAvailability(value: any): value is SimpleProductAvailability {
    return !!value && !!value.schedule && !!value.details && Array.isArray(value.events);
  }

  mapSimpleScheduleEventsToCalendarEntry(schedules: TeacherColLessonScheduleEvents[]): CalendarEntry[] {
    return schedules.reduce((entries, schedule) => {
      schedule.events.forEach((ev) => {
        entries.push(
          new CalendarEntry(
            schedule.schedule.id,
            ev.eventId,
            ev.eventDate,
            new Date(ev.eventDate.getTime() + ev.duration),
            () => schedule.schedule.name,
            null,
            schedule,
            'schedule'
          )
        );
      });
      return entries;
    }, [] as CalendarEntry[]);
  }

  //modals
  deleteWorktime() {
    this.loading = true;
    this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.deleteAvailabilityEvent(
            this.schoolId,
            this._teacherId,
            this.availabilityToDelete.schedule.id,
            this.eventIdToDelete,
            this.deleteRecurring
          )
        )
      )
      .subscribe({
        next: () => {
          this.availabilityToDelete = null;
          this.loadWeekEvents();
        },
        error: () => (this.loading = false),
      });
    this.availableWorktimeModal.hide();
  }

  saveWorktime() {
    this.loading = true;
    const productsArray = this.getTargetProductsForWorktime();

    merge(...productsArray.map((pCode) => this.saveWorktimeForProduct(pCode))).subscribe({
      next: () => this.loadWeekEvents(),
      error: () => (this.loading = false),
    });

    this.createWorktimeModal.hide();
  }

  getProductName(code: string): string {
    return LangProductMapper.mapLangCodeToLangName(code);
  }

  private getTargetProductsForWorktime(): string[] {
    return this.newWorktimeProduct === this.allProductsCode
      ? this.availableProducts
      : [this.newWorktimeProduct];
  }

  saveWorktimeForProduct(targetProduct: string) {
    const {startDate, finishDate} = this.buildWorktimeDateRange();

    const request = this.prepareWorktimeDefinition(
      targetProduct,
      startDate,
      finishDate
    );

    return this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.createAvailability(this.schoolId, this._teacherId, request)
        )
      );
  }

  private buildWorktimeDateRange(): {startDate: Date; finishDate: Date} {
    const startDate = this.setTimeWithStr(
      this.newWorktimeDate,
      this.newWorktimeStartTime
    );
    let finishDate = this.setTimeWithStr(
      this.newWorktimeDate,
      this.newWorktimeFinishTime
    );

    if (finishDate.getTime() < startDate.getTime()) {
      finishDate = this.setTimeWithStr(
        new Date(this.newWorktimeDate.getTime() + TeacherMainCalendarComponent.DAY_MS),
        this.newWorktimeFinishTime
      );
    }

    return {startDate, finishDate};
  }

  prepareWorktimeDefinition(
    targetProduct: string,
    startDate: Date,
    finishDate: Date
  ): ProductAvailabilityRequest {
    const duration = finishDate.getTime() - startDate.getTime();
    const competences = Array.from(
      new Set(
        this.teacherCourseProfiles
          .filter((profile) => CourseProductMapper.toProductCode(profile.course?.code) === targetProduct)
          .flatMap((profile) => profile.competences ?? [])
          .map((competence) => competence.code)
          .filter((competenceCode): competenceCode is string => !!competenceCode)
      )
    ).map((competenceCode) => ({code: competenceCode}));

    return {
      availabilityDetails: {
        product: {
          code: targetProduct,
          name: targetProduct,
        },
        competences,
        overtake: this.newWorktimeOvertake,
      },
      scheduleDetails: {
        recurring: this.createRecurringString(),
        ending: this.newRecurringFinishDate,
        duration,
        time: {
          starting: startDate,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      },
    };
  }

  createRecurringString() {
    const weekDays = Object.keys(this.newWorktimeDays)
      .filter((dayName) => this.newWorktimeDays[dayName]);
    return RecurringTypeUtils.getRecurring(this.newWorktimeRecurring, weekDays);
  }


  setTimeWithStr(date: Date, time: string) {
    const timeSplit = time.split(':');
    const hour = Number(timeSplit[0]);
    const minutes = Number(timeSplit[1]);

    const res = new Date(date);
    res.setHours(hour);
    res.setMinutes(minutes);

    return res;
  }

  getWeekDayName(day: string) {
    return `WEEKDAY.${day.toUpperCase()}`;
  }

  getCourse() {
    return this.eventChosen?.relatedObject?.details?.product?.name;
  }
}
